import { normalizeId } from './ids';
import { canWriteCard } from './permissions';

/**
 * Duplicate profiles (keepr-api docs/SCHEMA.md § Quick add templates,
 * "Duplicate profiles"). A profile IS a quick add template — the same
 * document, editor and resolve — that copies from an existing item: it reads
 * `{{source.*}}` or copies everything (`copyAll`). The API derives
 * `duplicateProfile` and stores it, so the client never re-parses formulas to
 * tell one apart.
 *
 * Because a profile needs an item to copy, it is offered from an item's
 * Duplicate ▾ and nowhere else: never in a quick add launch row, never on
 * Home (selectQuickAddTemplates excludes it).
 */

const COLLECTION = 'collection';

export const isDuplicateProfile = template => template?.duplicateProfile === true;

/**
 * The card and its ancestors, nearest first. A profile of a parent card
 * applies to the items of its descendants too — the API accepts a source of
 * the template's card or any card under it.
 */
export function cardLineage(cardsById, cardId) {
	const out = [];
	const seen = new Set();
	let current = normalizeId(cardId);
	while (current && !seen.has(current) && out.length < 20) {
		seen.add(current);
		out.push(current);
		current = normalizeId(cardsById?.[current]?.parentCardId);
	}
	return out;
}

/**
 * The profiles that can duplicate an item of `cardId`, ordered for a menu:
 * the card's own before an ancestor's, collection tier before personal, name
 * order within each.
 *
 * WHO MAY RUN ONE — the API's rule exactly (keepr-api
 * quickAddTemplateUtils resolveQuickAddTemplate, docs/SCHEMA.md "Duplicate
 * profiles", "Who may launch one"): the caller can see the template (it is
 * in their list), can read the source (it is the item on screen) and can
 * WRITE THE ITEM's card — the card the copy is made on. Write on the
 * PROFILE's card is not asked: a writer of a child card alone is offered,
 * and defaulted to, a parent card's profile on the child's items, because
 * the API runs it for them. Nothing is offered when the caller cannot write
 * the item's card (Duplicate itself is hidden then). A profile whose card is
 * not in the definitions cache is left out too: the lineage walk cannot see
 * past a card it does not have.
 *
 * Returns [{ template, depth, scope }] — `depth` 0 is the item's own card.
 */
export function selectDuplicateProfiles({ collection = null, templates, cardsById, cardId }) {
	const lineage = cardLineage(cardsById, cardId);
	if (!lineage.length) return [];
	if (collection && !canWriteCard(collection, lineage[0])) return [];
	const depthOf = new Map(lineage.map((id, depth) => [id, depth]));
	return (templates || [])
		.filter(template => isDuplicateProfile(template) && !template?.pending)
		.map(template => {
			const templateCard = normalizeId(template?.card_id);
			if (!depthOf.has(templateCard)) return null;
			// The card itself must be one the cache knows (the item's own card
			// always is on a surface that renders it; an ancestor might not be
			// when a card-scoped grant hides it).
			if (cardsById && !cardsById[templateCard]) return null;
			return {
				template,
				depth: depthOf.get(templateCard),
				scope: template?.scope === COLLECTION ? COLLECTION : 'user',
			};
		})
		.filter(Boolean)
		.sort((a, b) => (
			a.depth - b.depth
			|| (a.scope === COLLECTION ? 0 : 1) - (b.scope === COLLECTION ? 0 : 1)
			|| (a.template?.name || '').localeCompare(b.template?.name || '')
		));
}

/**
 * What a plain click on Duplicate runs: the nearest card's default profile —
 * the item's own card's, else its parent's, and so on (a descendant card
 * inherits its ancestor's default unless it has its own). Null when none is
 * set: plain Duplicate.
 */
export function defaultDuplicateProfile(profiles) {
	const hit = (profiles || []).find(entry => entry?.template?.defaultForDuplicate === true && entry.scope === COLLECTION);
	return hit ? hit.template : null;
}

/**
 * Where a profile launch lands: the new-item form for the SOURCE's card (a
 * duplicate is a sibling of what it copies), with the template and the
 * source in the query — the form resolves both on the API itself, so the
 * link survives a reload.
 */
export const duplicateProfilePath = (collectionId, cardId, template, sourceItemId) => (
	`/collections/${normalizeId(collectionId)}/items/new/${normalizeId(cardId)}`
	+ `?template=${normalizeId(template?._id)}&source=${normalizeId(sourceItemId)}`
);

/** Navigates to a profile launch; the name rides along for the form's header line. */
export function launchDuplicateProfile(navigate, { collectionId, cardId, template, sourceItemId }) {
	navigate(duplicateProfilePath(collectionId, cardId, template, sourceItemId), {
		state: { quickAdd: { _id: normalizeId(template?._id), name: template?.name || '', duplicateProfile: true } },
	});
}
